/** Profil (Screen mieter/Einstellungen, MVP): Kontakt, Adresse, IBAN (maskiert, protokolliert), Auszahlungsweg, privat/gewerblich (nur Laden), Abmelden. Benachrichtigungen/Datenschutz-Center sind V2. */
import { useState, type FormEvent } from "react";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { useAbmelden } from "../api/ich";
import { useMieterProfil, useProfilSpeichern, type MieterProfil } from "./api";
import { initialen } from "../laden/Mieter";
import { useCockpit, usePortalSpeichern } from "./cockpit-api";
import { pushAktiv, pushAusschalten, pushEinschalten, usePublicConfig, useMandat, useMandatAktion } from "./konto-api";
import { useFeedbackSenden } from "./portal-c-api";
import { Textarea } from "../ui/Layout";
import { useEffect } from "react";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { Link } from "react-router";
import { KACHELN, type Kachel } from "@shared/schemas/mieterportal";
import { SPRACHEN, spracheMerken, useT, type Sprache, useTx } from "../i18n";
import { KACHEL } from "./Start";
import { Icon } from "../ui";

export function Profil() {
  const q = useMieterProfil();
  return <Seitenzustand query={q} lines={5}>{(p) => <ProfilFormular p={p} />}</Seitenzustand>;
}

function ProfilFormular({ p }: { p: MieterProfil }) {
  const tx = useTx();
  const t = useT();
  const speichern = useProfilSpeichern();
  const abmelden = useAbmelden();
  const [form, setForm] = useState({ telefon: p.telefon, strasse: p.adresse.strasse ?? "", plz: p.adresse.plz ?? "", ort: p.adresse.ort ?? "", kontoinhaber: p.kontoinhaber, auszahlungsweg: p.auszahlungsweg, iban: "" });
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setErfolg(false);
    try {
      await speichern.mutateAsync({ telefon: form.telefon.trim(), adresse: { strasse: form.strasse.trim(), plz: form.plz.trim(), ort: form.ort.trim() }, kontoinhaber: form.kontoinhaber.trim(), auszahlungsweg: form.auszahlungsweg, iban: form.iban.trim() || undefined });
      setForm({ ...form, iban: "" });
      setErfolg(true);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
    }
  }

  return (
    <form className="sc-col" style={{ gap: "var(--space-4)" }} onSubmit={(e) => void absenden(e)} noValidate>
      <h1 className="title">{t("profil.titel")}</h1>
      <div className="sc-row">
        <span className="sc-avatar sc-avatar--lg">{initialen(p.name)}</span>
        <div className="sc-col" style={{ gap: 0 }}>
          <span className="sc-strong">{p.name}</span>
          <span className="sc-muted sc-small">Mieternummer {p.mieternummer} · {p.email}</span>
          <span><StatusBadge status="neutral">{p.gewerblich ? "gewerblich" : "privat"}</StatusBadge></span>
        </div>
      </div>
      {erfolg ? <Notice tone="success">Gespeichert.</Notice> : null}
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Card title={t("profil.kontakt")}>
        <p className="sc-muted sc-small">Name, E-Mail und privat/gewerblich ändert der Laden für dich.</p>
        <FormGrid>
          <TextField label="Telefon" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} inputMode="tel" autoComplete="tel" />
          <TextField label="Straße und Hausnummer" value={form.strasse} onChange={(e) => setForm({ ...form, strasse: e.target.value })} autoComplete="street-address" />
          <TextField label="PLZ" value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} inputMode="numeric" autoComplete="postal-code" />
          <TextField label="Ort" value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} autoComplete="address-level2" />
        </FormGrid>
      </Card>
      <Card title={t("profil.auszahlung")}>
        <FormGrid>
          <Select label="So möchtest du dein Guthaben bekommen" value={form.auszahlungsweg} onChange={(e) => setForm({ ...form, auszahlungsweg: e.target.value as "ueberweisung" | "bar" })}>
            <option value="ueberweisung">Überweisung</option>
            <option value="bar">Bar im Laden</option>
          </Select>
          <TextField label="Kontoinhaber" value={form.kontoinhaber} onChange={(e) => setForm({ ...form, kontoinhaber: e.target.value })} autoComplete="name" />
          <TextField label={p.ibanMaske ? `IBAN (hinterlegt: ${p.ibanMaske})` : "IBAN"} value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder={p.ibanMaske ? "Nur bei Änderung eingeben" : "DE89 3704 0044 0532 0130 00"} autoComplete="off" hint="Wird verschlüsselt gespeichert und nur maskiert angezeigt" />
        </FormGrid>
      </Card>
      <Button type="submit" variant="primary" block icon="check" loading={speichern.isPending}>{t("profil.speichern")}</Button>
      <SpracheAnzeigeKarte />
      <KachelnKarte />
      <div className="sc-col" style={{ gap: 6 }}>
        <Link to="/mieter/sicherheit" className="mp-link-zeile"><Icon name="shield-check" size={20} /><span style={{ flex: 1 }}>{t("profil.sicherheit")}</span><Icon name="chevron-right" size={18} /></Link>
        <Link to="/mieter/werben" className="mp-link-zeile"><Icon name="users" size={20} /><span style={{ flex: 1 }}>{t("profil.werben")}</span><Icon name="chevron-right" size={18} /></Link>
        <Link to="/mieter/sammel" className="mp-link-zeile"><Icon name="inbox" size={20} /><span style={{ flex: 1 }}>{t("kachel.sammel")}</span><Icon name="chevron-right" size={18} /></Link>
      </div>
      <PortalKarte />
      <ZielKarte />
      <MandatKarte ibanVorhanden={!!p.ibanMaske} />
      <BenachrichtigungenKarte />
      <FeedbackKarte />
      <Card title={tx("Datenschutz")}>
        <p className="sc-muted sc-small">Datenauskunft und das Entfernen deiner Personendaten nach Vertragsende macht der Laden für dich (DSGVO). Deine Verkäufe und Abrechnungen findest du unter Kontoauszug als PDF und CSV.</p>
      </Card>
      <Button variant="secondary" block icon="log-out" onClick={() => void abmelden()}>{t("profil.abmelden")}</Button>
      <p className="sc-muted sc-small sc-center"><Link to="/impressum">Impressum</Link> · <Link to="/datenschutz">Datenschutz</Link></p>
    </form>
  );
}

/** Freude-Momente und Startseite: Konfetti, Ton, Checkliste wieder einblenden. */
function PortalKarte() {
  const tx = useTx();
  const ck = useCockpit();
  const speichern = usePortalSpeichern();
  const e = ck.data?.einstellungen;
  if (!e) return null;
  return (
    <Card title={tx("Freude-Momente")}>
      <label className="sc-check"><input type="checkbox" checked={e.konfetti} onChange={(ev) => speichern.mutate({ ...e, konfetti: ev.target.checked })} /> Konfetti bei „Verkauft!“</label>
      <label className="sc-check"><input type="checkbox" checked={e.ton} onChange={(ev) => speichern.mutate({ ...e, ton: ev.target.checked })} /> Kurzer Ton bei „Verkauft!“</label>
      {e.onboardingAusgeblendet ? <Button variant="quiet" onClick={() => speichern.mutate({ ...e, onboardingAusgeblendet: false })}>Checkliste auf Start wieder zeigen</Button> : null}
    </Card>
  );
}

/** Push aufs Handy, Rhythmus, Wochenmail. */
function BenachrichtigungenKarte() {
  const tx = useTx();
  const ck = useCockpit();
  const cfg = usePublicConfig();
  const speichern = usePortalSpeichern();
  const [aktiv, setAktiv] = useState<boolean | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    void pushAktiv().then(setAktiv);
  }, []);
  const e = ck.data?.einstellungen;
  if (!e) return null;
  const moeglich = !!cfg.data?.vapidPublicKey && "PushManager" in window;
  const iOSOhneApp = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.matchMedia("(display-mode: standalone)").matches;
  async function umschalten() {
    setLaeuft(true);
    setMeldung(null);
    try {
      if (aktiv) {
        await pushAusschalten();
        setAktiv(false);
      } else {
        const r = await pushEinschalten(cfg.data!.vapidPublicKey!);
        if (r === "ok") setAktiv(true);
        else setMeldung(r === "verweigert" ? "Benachrichtigungen sind im Browser blockiert – in den Einstellungen des Handys erlauben." : "Dieses Gerät unterstützt keine Push-Nachrichten.");
      }
    } catch {
      setMeldung("Das hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }
  return (
    <Card title={tx("Benachrichtigungen")}>
      {meldung ? <Notice tone="attention">{meldung}</Notice> : null}
      {iOSOhneApp ? <p className="sc-muted sc-small">Auf dem iPhone zuerst „Zum Home-Bildschirm“ hinzufügen – dann gehen Push-Nachrichten.</p> : null}
      <div className="sc-spread">
        <span><span className="sc-strong">Push aufs Handy</span><br /><span className="sc-muted sc-small">„Verkauft!“ sofort, auch wenn das Portal zu ist</span></span>
        <Button variant={aktiv ? "secondary" : "primary"} icon="bell" loading={laeuft || aktiv === null} disabled={!moeglich} onClick={() => void umschalten()}>{aktiv ? "Ausschalten" : "Einschalten"}</Button>
      </div>
      <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }} role="radiogroup" aria-label="Wann benachrichtigen">
        <button type="button" className="sc-pill" aria-pressed={e.push === "sofort"} onClick={() => speichern.mutate({ ...e, push: "sofort" })}>Sofort bei jedem Verkauf</button>
        <button type="button" className="sc-pill" aria-pressed={e.push === "taeglich"} onClick={() => speichern.mutate({ ...e, push: "taeglich" })}>Einmal täglich, abends</button>
        <button type="button" className="sc-pill" aria-pressed={e.push === "aus"} onClick={() => speichern.mutate({ ...e, push: "aus" })}>Aus</button>
      </div>
      <label className="sc-check" style={{ marginTop: "var(--space-3)" }}><input type="checkbox" checked={e.wochenmail} onChange={(ev) => speichern.mutate({ ...e, wochenmail: ev.target.checked })} /> Sonntags eine Wochen-Zusammenfassung per E-Mail</label>
    </Card>
  );
}

/** Kurzes Feedback an den Laden oder an uns. */
function FeedbackKarte() {
  const tx = useTx();
  const senden = useFeedbackSenden();
  const [an, setAn] = useState<"laden" | "anbieter">("laden");
  const [text, setText] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  return (
    <Card title={tx("Feedback")}>
      {meldung ? <Notice tone="success">{meldung}</Notice> : null}
      <div className="sc-toolbar" role="radiogroup" aria-label="Empfänger">
        <button type="button" className="sc-pill" aria-pressed={an === "laden"} onClick={() => setAn("laden")}>An den Laden</button>
        <button type="button" className="sc-pill" aria-pressed={an === "anbieter"} onClick={() => setAn("anbieter")}>An MeinRegalladen (App)</button>
      </div>
      <Textarea label={an === "laden" ? "Was fehlt dir im Laden?" : "Was fehlt dir in der App?"} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} />
      <Button variant="secondary" icon="mail" loading={senden.isPending} disabled={text.trim().length < 3} onClick={() => senden.mutate({ an, text: text.trim() }, { onSuccess: () => { setText(""); setMeldung("Danke – ist angekommen."); }, onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Senden hat nicht geklappt.") })}>Senden</Button>
    </Card>
  );
}

/** Verkaufsziel (MP-D): Betrag je Tag, Woche oder Monat – Push, sobald erreicht. */
function ZielKarte() {
  const tx = useTx();
  const ck = useCockpit();
  const speichern = usePortalSpeichern();
  const e = ck.data?.einstellungen;
  const [betrag, setBetrag] = useState<string | null>(null);
  if (!e) return null;
  const wert = betrag ?? (e.ziel.betragCent ? (e.ziel.betragCent / 100).toFixed(2).replace(".", ",") : "");
  return (
    <Card title={tx("Mein Verkaufsziel")} id="ziel">
      <p className="sc-muted sc-small">Ein Ziel motiviert. Du siehst den Fortschritt auf der Startseite und bekommst eine Nachricht, sobald es erreicht ist.</p>
      <FormGrid>
        <Select label="Zeitraum" value={e.ziel.zeitraum} onChange={(ev) => speichern.mutate({ ...e, ziel: { ...e.ziel, zeitraum: ev.target.value as "tag" | "woche" | "monat" } })}>
          <option value="tag">Pro Tag</option>
          <option value="woche">Pro Woche</option>
          <option value="monat">Pro Monat</option>
        </Select>
        <TextField label="Betrag (leer = kein Ziel)" value={wert} onChange={(ev) => setBetrag(ev.target.value)} onBlur={() => { const c = wert.trim() ? parseEuro(wert) : 0; if (c !== null && c !== e.ziel.betragCent) speichern.mutate({ ...e, ziel: { ...e.ziel, betragCent: c } }); setBetrag(null); }} inputMode="decimal" suffix="€" />
      </FormGrid>
      {e.ziel.betragCent ? <p className="sc-small" style={{ margin: 0 }}>Aktuell: {formatEuro(e.ziel.betragCent)} {e.ziel.zeitraum === "tag" ? "pro Tag" : e.ziel.zeitraum === "woche" ? "pro Woche" : "pro Monat"}</p> : null}
    </Card>
  );
}

/** Sprache und Anzeige (MP-E): EN/UK/TR für die wichtigsten Screens, große Schrift, Einfach-Modus. */
function SpracheAnzeigeKarte() {
  const t = useT();
  const ck = useCockpit();
  const speichern = usePortalSpeichern();
  const e = ck.data?.einstellungen;
  if (!e) return null;
  return (
    <Card title={`${t("profil.sprache")} · ${t("profil.anzeige")}`}>
      <FormGrid>
        <Select label={t("profil.sprache")} value={e.sprache} onChange={(ev) => { const s = ev.target.value as Sprache; spracheMerken(s); speichern.mutate({ ...e, sprache: s }); }}>
          {SPRACHEN.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Select>
      </FormGrid>
      {e.sprache !== "de" ? <p className="sc-muted sc-small">Noch nicht alles ist übersetzt – was fehlt, erscheint auf Deutsch.</p> : null}
      <label className="sc-check"><input type="checkbox" checked={e.anzeige.grosseSchrift} onChange={(ev) => speichern.mutate({ ...e, anzeige: { ...e.anzeige, grosseSchrift: ev.target.checked } })} /> {t("profil.grosseSchrift")}</label>
      <label className="sc-check"><input type="checkbox" checked={e.anzeige.einfach} onChange={(ev) => speichern.mutate({ ...e, anzeige: { ...e.anzeige, einfach: ev.target.checked } })} /> {t("profil.einfach")}</label>
    </Card>
  );
}

/** Eigene Startseiten-Kacheln (MP-E): an/aus und Reihenfolge. */
function KachelnKarte() {
  const t = useT();
  const ck = useCockpit();
  const speichern = usePortalSpeichern();
  const e = ck.data?.einstellungen;
  if (!e) return null;
  const aktiv = e.kacheln;
  const setzen = (k: Kachel[]) => speichern.mutate({ ...e, kacheln: k });
  const bewegen = (i: number, d: -1 | 1) => {
    const k = [...aktiv];
    const j = i + d;
    if (j < 0 || j >= k.length) return;
    [k[i], k[j]] = [k[j]!, k[i]!];
    setzen(k);
  };
  return (
    <Card title={t("profil.kacheln")}>
      <p className="sc-muted sc-small">Welche Kacheln auf Start stehen und in welcher Reihenfolge (höchstens 10). Die erste ist groß.</p>
      <div className="sc-col" style={{ gap: 2 }}>
        {aktiv.map((k, i) => (
          <div key={k} className="sc-spread" style={{ padding: "4px 0" }}>
            <span className="sc-row"><Icon name={KACHEL[k].icon} size={18} /> {t(KACHEL[k].text)}</span>
            <span className="sc-row" style={{ gap: 2 }}>
              <Button variant="quiet" aria-label="Nach oben" disabled={i === 0} onClick={() => bewegen(i, -1)}>↑</Button>
              <Button variant="quiet" aria-label="Nach unten" disabled={i === aktiv.length - 1} onClick={() => bewegen(i, 1)}>↓</Button>
              <Button variant="quiet" icon="x" aria-label="Kachel entfernen" disabled={aktiv.length <= 2} onClick={() => setzen(aktiv.filter((x) => x !== k))} />
            </span>
          </div>
        ))}
      </div>
      {aktiv.length < 10 && KACHELN.some((k) => !aktiv.includes(k)) ? (
        <div className="sc-toolbar" style={{ marginTop: "var(--space-2)" }}>
          {KACHELN.filter((k) => !aktiv.includes(k)).map((k) => <button key={k} type="button" className="sc-pill" onClick={() => setzen([...aktiv, k])}>+ {t(KACHEL[k].text)}</button>)}
        </div>
      ) : null}
    </Card>
  );
}

/** SEPA-Lastschriftmandat (E9): Miete bequem einziehen lassen statt überweisen. */
function MandatKarte({ ibanVorhanden }: { ibanVorhanden: boolean }) {
  const tx = useTx();
  const q = useMandat();
  const aktion = useMandatAktion();
  const [haken, setHaken] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const d = q.data;
  if (!d) return null;
  if (!d.glaeubigerId) return null;
  return (
    <Card title={tx("Miete per Lastschrift")}>
      {meldung ? <Notice tone="info">{meldung}</Notice> : null}
      {d.mandat ? (
        <>
          <p style={{ margin: 0 }}>Aktiv seit {formatTag(d.mandat.erteiltAt.slice(0, 10))} · Mandatsreferenz <span className="sc-strong">{d.mandat.referenz}</span> · Konto {d.mandat.ibanMaske}</p>
          <p className="sc-muted sc-small">Vor jedem Einzug bekommst du eine Ankündigung per E-Mail. Gläubiger-ID des Ladens: {d.glaeubigerId}.</p>
          <Button variant="quiet" icon="x" loading={aktion.isPending} onClick={() => confirm("Mandat widerrufen? Offene Miete musst du dann überweisen.") && aktion.mutate({ art: "widerrufen", id: d.mandat!.id }, { onSuccess: () => setMeldung("Mandat widerrufen.") })}>Mandat widerrufen</Button>
        </>
      ) : (
        <>
          <p className="sc-muted sc-small">Offene Miete wird dann automatisch von deinem Konto eingezogen, wenn dein Verkaufserlös nicht reicht. Du bekommst jedes Mal vorher eine E-Mail.</p>
          {!ibanVorhanden ? <p className="sc-small">Erst oben deine IBAN eintragen und speichern.</p> : null}
          <label className="sc-check"><input type="checkbox" checked={haken} onChange={(e) => setHaken(e.target.checked)} disabled={!d.moeglich} /> Ich ermächtige den Laden, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die gezogenen Lastschriften einzulösen. Ich kann innerhalb von acht Wochen ab Belastung die Erstattung verlangen.</label>
          <Button variant="primary" icon="check" disabled={!haken || !d.moeglich} loading={aktion.isPending} onClick={() => aktion.mutate({ art: "erteilen" }, { onSuccess: (r) => setMeldung(`Mandat erteilt. Referenz ${(r as { referenz: string }).referenz}.`), onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Das hat nicht geklappt.") })}>Mandat erteilen</Button>
        </>
      )}
    </Card>
  );
}
